import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--growth/confluence-side-nav';
import { type NextBestAction } from '@post-office/confluence-next-best-action';

import {
	assertRovoOnboardingEligibility,
	type RovoOnboardingEligibilityMessage,
	type RovoOnboardingEligibilityRequest,
} from '../../../common/rovo-onboarding-eligibility';
import { RovoOnboardingBanner } from './components/rovo-onboarding-banner';
import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import { type MessageTemplateContext } from '../../../index';

const hydrateStage = async ({
	message,
	request,
}: {
	message: {
		messageInstanceId: string;
		messageTemplateId: string;
		triggerId: string;
		context: unknown;
	};
	/** Placement request shape; restApiClients is typed as unknown by the framework. Cast to RovoOnboardingEligibilityRequest when calling shared eligibility. */
	request: Omit<RovoOnboardingEligibilityRequest, 'restApiClients'> & {
		restApiClients?: unknown;
	};
}) => {
	// Framework provides message.context as template context (MessageTemplateContext); cast for shared eligibility API.
	await assertRovoOnboardingEligibility(
		request as RovoOnboardingEligibilityRequest,
		message as RovoOnboardingEligibilityMessage,
		'Confluence Rovo onboarding banner',
	);
	return message.context as MessageTemplateContext;
};

/** Exposed for unit testing the hydrate stage. */
export const __hydrateStageForTest = hydrateStage;

export const MessageBackend = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: assertMessageTemplateContext,
		stage: (input) => {
			const context = input.message.context as MessageTemplateContext;
			return {
				goalMetadata: {
					primaryGoal: {
						goalId: 'clickthrough',
					},
				},
				nextBestAction: context.nextBestActionType,
			};
		},
	},
	hydrate: {
		validate: assertMessageTemplateContext,
		stage: hydrateStage,
	},
}));

type MessageFrontendProps = MessageTemplateContext & {
	handleOnDismissed: () => void;
	onSideNavClick: (nextBestActionType: NextBestAction) => string | void | null;
};

export const MessageFrontend = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: ({
			nextBestActionType,
			handleOnDismissed,
			onSideNavClick,
		}: MessageFrontendProps) => {
			return (
				<RovoOnboardingBanner
					action={nextBestActionType}
					onPrimaryActionClick={onSideNavClick}
					onDismissClick={handleOnDismissed}
				/>
			);
		},
	},
});
